import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { QueryKeys, SURVEY_ID_HEADER } from '../constants';
import { useFetch } from './utils';

export function useCreateSurvey() {
  const fetcher = useFetch();
  return useMutation({
    mutationFn: ({ data }) => fetcher({ path: 'v1/surveys/create/', method: 'POST', data }),
  });
}

export function useArchiveSurvey() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surveyId, data }) =>
      fetcher({
        path: `v1/surveys/${surveyId}/archive/`,
        method: 'PUT',
        data: JSON.stringify(data),
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    onSettled: (_data, _error, { surveyId }) => {
      queryClient.invalidateQueries([QueryKeys.SURVEYS]);
      queryClient.invalidateQueries([QueryKeys.SURVEY, { surveyId }]);
    },
    onError: () => {
      message.error('Something went wrong');
    },
  });
}

export function useUpdateSurveySummary() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surveyId, data }) =>
      fetcher({
        path: `v1/surveys/${surveyId}/update-summary/`,
        method: 'PATCH',
        data: JSON.stringify(data),
      }),
    onSettled: (_data, _error, { surveyId }) =>
      queryClient.invalidateQueries([QueryKeys.SURVEY_SUMMARY, { surveyId }]),
    onError: () => {
      message.error('Something went wrong updating survey summary');
    },
  });
}

export function useSurveys(options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.SURVEYS],
    queryFn: () => fetcher({ path: 'v1/surveys/' }),
    ...options,
  });
}

export function useQuestions({ surveyId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.QUESTIONS, { surveyId }],
    queryFn: () =>
      fetcher({
        path: `v1/surveys/${surveyId}/questions/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    ...options,
  });
}

export function useSurveyClients({ surveyId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.SURVEY_CLIENTS, { surveyId }],
    queryFn: () => fetcher({ path: `v1/surveys/${surveyId}/clients/` }),
    ...options,
  });
}

export function useSurveyInvitations({ surveyId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.SURVEY_INVITATIONS, { surveyId }],
    queryFn: () => fetcher({ path: `v1/surveys/${surveyId}/invitations/` }),
    ...options,
  });
}

export function useSurveySummary({ surveyId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.SURVEY_SUMMARY, { surveyId }],
    queryFn: () =>
      fetcher({
        path: `v1/surveys/${surveyId}/summary/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    ...options,
  });
}

export function useSurvey({ surveyId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.SURVEY, { surveyId }],
    queryFn: () =>
      fetcher({
        path: `v1/surveys/${surveyId}/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    ...options,
  });
}

export const usePublishSurvey = () => {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surveyId }) =>
      fetcher({ path: `v1/surveys/${surveyId}/publish/`, method: 'PUT' }),
    onSettled: (_data, _error, { surveyId }) => {
      queryClient.invalidateQueries([QueryKeys.SURVEY, { surveyId }]);
      queryClient.invalidateQueries([QueryKeys.SURVEYS]);
    },
    onError: () => {
      message.error('Something went wrong publishing survey');
    },
  });
};

export const useUpdateSurvey = () => {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surveyId, data }) =>
      fetcher({
        path: `v1/surveys/${surveyId}/update/`,
        method: 'PATCH',
        data: JSON.stringify(data),
      }),
    onSettled: (_data, _error, { surveyId }) => {
      queryClient.invalidateQueries([QueryKeys.SURVEY, { surveyId }]);
      queryClient.invalidateQueries([QueryKeys.SURVEYS]);
    },
    onError: () => {
      message.error('Something went wrong updating survey');
    },
  });
};

export const useUpdateGuest = () => {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, surveyId, data }) =>
      fetcher({
        path: `v1/surveys/${surveyId}/clients/${userId}/update/`,
        method: 'PATCH',
        data: JSON.stringify(data),
      }),
    onSettled: (_data, _error, { surveyId }) =>
      queryClient.invalidateQueries([QueryKeys.SURVEY_CLIENTS], { surveyId }),
    onError: () => {
      message.error('Something went wrong updating guest');
    },
  });
};

export const useDeleteGuest = () => {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, surveyId }) =>
      fetcher({
        path: `v1/surveys/${surveyId}/clients/${userId}/delete/`,
        method: 'DELETE',
      }),
    onSettled: (_data, _error, { surveyId }) =>
      queryClient.invalidateQueries([QueryKeys.SURVEY_CLIENTS], { surveyId }),
    onError: () => {
      message.error('Something went wrong removing guest');
    },
  });
};
